/**
 * useServiceWorkerUpdate — listens for the `sw-update-available` window event
 * dispatched from index.html when a new service worker is waiting,
 * and exposes an `applyUpdate` action that activates it.
 *
 * Flow:
 *   1. SW lifecycle in index.html detects a new SW reaching the 'installed'
 *      state (with an existing controller — i.e. a real update, not first install)
 *   2. index.html dispatches `sw-update-available` and stashes the waiting
 *      worker on `window.__swWaiting`
 *   3. This hook flips `updateAvailable` to true → UpdateBanner renders
 *   4. User taps "refresh" → applyUpdate activates the waiting SW through the
 *      boot script's `window.__sendlyActivateWaiting` (it asks the worker's
 *      build id, then posts SKIP_WAITING, and records the activation so a chunk
 *      failure meanwhile waits for it instead of activating a second time)
 *   5. New SW activates → fires `controllerchange` (handled in index.html) →
 *      page reloads with the new bundle, unless the page already runs its build
 *
 * docs/spec/01c-sw-update-flow.md, "Worker activation".
 */
import { useState, useEffect, useCallback } from "react";

/**
 * One event of the update trace (docs/spec/01d-update-trace.md). The boot
 * script owns the record; absent on the dev server, and it never throws.
 */
export function traceUpdate(kind, data) {
  try {
    if (typeof window !== "undefined" && typeof window.__sendlyTrace === "function") window.__sendlyTrace(kind, data);
  } catch {
    // tracing must never break the update
  }
}

function workerState(worker) {
  try {
    return worker && typeof worker.state === "string" ? worker.state : null;
  } catch {
    return null;
  }
}

export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const onAvailable = () => setUpdateAvailable(true);
    window.addEventListener("sw-update-available", onAvailable);
    // Edge case: the event fired before this hook mounted (race during slow
    // initial render). The waiting SW is stashed on window.__swWaiting so we
    // can detect it after the fact.
    if (typeof window !== "undefined" && window.__swWaiting) {
      setUpdateAvailable(true);
    }
    return () => window.removeEventListener("sw-update-available", onAvailable);
  }, []);

  const applyUpdate = useCallback(async () => {
    // Safety-net watchdog — ensures the page always moves on even if the SW
    // path hangs. iOS reaps the waiting SW's background process during
    // lock/sleep cycles; SKIP_WAITING posted to a dead worker is silently
    // lost, and a blind reload would bring the OLD version back (the banner
    // then reappears on the next update check — perceived as a "second
    // update"). So on timeout, branch on how far the swap got:
    //   - activation started → the smooth controllerchange reload is just
    //     slow; a plain reload lands on the new version.
    //   - still stuck in 'installed' → run the full stale-recovery
    //     (unregister SWs + purge Cache Storage + cache-busted reload),
    //     which is guaranteed to fetch and boot the newest deploy.
    // Cleared by index.html's controllerchange listener if the smooth path
    // beats us to the reload.
    let waitingWorker = null;
    const fallback = setTimeout(() => {
      const state = waitingWorker && waitingWorker.state;
      if (state !== "activating" && state !== "activated" && typeof window.__sendlyChunkRecovery === "function") {
        traceUpdate("fb", { ws: workerState(waitingWorker), act: "recovery" });
        window.__sendlyChunkRecovery();
        // Last resort: the recovery no-ops inside its cooldown/attempt
        // guard — never leave the user on a frozen banner click.
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      traceUpdate("fb", { ws: workerState(waitingWorker), act: "reload" });
      window.location.reload();
    }, 3000);
    try {
      if (!("serviceWorker" in navigator)) {
        clearTimeout(fallback);
        window.location.reload();
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      const waiting = reg?.waiting || (typeof window !== "undefined" ? window.__swWaiting : null);
      if (waiting) {
        waitingWorker = waiting;
        traceUpdate("tap", { ws: workerState(waiting), br: "activate" });
        // The boot script's one activation helper, without its stuck-waiting
        // watchdog: the fallback above is this tap's own. Absent only where the
        // boot script skips its service-worker block (local hosts) — then the
        // direct post.
        const activate = window.__sendlyActivateWaiting;
        if (typeof activate === "function") {
          if (!activate(waiting, { watchdog: false })) throw new Error("SKIP_WAITING not delivered");
        } else {
          waiting.postMessage({ type: "SKIP_WAITING" });
        }
        // controllerchange handler in index.html performs the reload before
        // the fallback fires. Leave the timeout armed as a safety net.
        return;
      }
      traceUpdate("tap", { ws: null, br: "reload" });
      clearTimeout(fallback);
      window.location.reload();
    } catch {
      traceUpdate("tap", { ws: workerState(waitingWorker), br: "failed" });
      clearTimeout(fallback);
      window.location.reload();
    }
  }, []);

  return { updateAvailable, applyUpdate };
}
